import { useAuth0 } from '@auth0/auth0-react';
import { deepMerge } from 'utils/deepMerge';
import { getConfig } from 'utils/config';
import { loginConfig } from 'auth0/login-config';
import { getErrorMessage } from 'utils/errors';
import { useMemo, useRef, useEffect } from 'react';
import { UseGetOptions } from './useStatelessGet';

// Static objects for default parameters to prevent re-renders
const emptyObject = {};
const defaultOptions = {
  useApiUrl: true,
  skipAuth: false,
  headers: emptyObject,
};

export const useDeferredGet = <T>(options: UseGetOptions = defaultOptions) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const ref = useRef({ options });

  useEffect(() => {
    ref.current = { options };
  });

  const get = useMemo(() => {
    const { useApiUrl, skipAuth, headers } = deepMerge(defaultOptions, ref.current.options);
    const { apiUrl } = getConfig();
    const initialHeaders: HeadersInit = {
      Accept: 'application/json',
    };

    return async (url: string, params: URLSearchParams = new URLSearchParams()): Promise<T> => {
      try {
        if (skipAuth !== true) {
          const token = await getAccessTokenSilently();
          initialHeaders.Authorization = `Bearer ${token}`;
        }
      } catch {
        await loginWithRedirect(loginConfig(window.location.pathname));
        throw new Error('Not logged in. Redirecting...');
      }

      const fetchUrl = useApiUrl ? `${apiUrl}${url}` : url;
      const config = deepMerge(
        {
          headers: initialHeaders,
          method: 'GET',
        },
        { headers },
      );
      const compiledUrl = params.toString() ? `${fetchUrl}?${params.toString()}` : fetchUrl;

      return fetch(compiledUrl, config).then(async (resp) => {
        let body;

        if (resp.headers.get('content-type')?.includes('application/json')) {
          body = await resp.json();
        } else {
          body = await resp.text();
        }

        if (resp.status !== 200 && resp.status !== 204) {
          throw new Error(getErrorMessage(body), { cause: resp });
        }

        return body;
      });
    };
  }, [getAccessTokenSilently, loginWithRedirect]);

  return get;
};
